/**
 * Helpers for GET /api/app-coins/apps (per-app rows + user total DreameyDiamonds).
 */
export const DREAMEY_VIDS_APP_ID = 2;

/** Matches main_ClaimRankDiamondReward tier amounts — used to split rank vs base/downline client-side. */
export const RANK_REWARD_ID_TO_DIAMONDS = {
  "rank-leader": 100,
  "rank-team-leader": 250,
  "rank-silver-exec": 500,
  "rank-gold-exec": 1500,
  "rank-diamond-exec": 2500,
};

/** Sum of one-time rank-tier diamonds from claimed reward ids (per app). */
export function sumRankDiamondsFromRewardIds(ids) {
  if (!Array.isArray(ids)) return 0;
  return ids.reduce((sum, id) => {
    if (typeof id !== "string") return sum;
    const n = RANK_REWARD_ID_TO_DIAMONDS[id];
    return sum + (typeof n === "number" ? n : 0);
  }, 0);
}

/**
 * Sum of DreameyDiamonds across all [main].[AppCoins] rows for the user
 * (NULL treated as 0 per row). Prefers `dreameyDiamondsSum` from the API;
 * otherwise sums the `apps` array.
 */
export function dreameyDiamondsUserSum(apiResponse) {
  if (!apiResponse || typeof apiResponse !== "object") return 0;
  const fromApi =
    apiResponse.dreameyDiamondsSum ??
    apiResponse.DreameyDiamondsSum ??
    apiResponse.totalDreameyCoinsSum ??
    apiResponse.TotalDreameyCoinsSum;
  if (fromApi != null) {
    const n = Number(fromApi);
    if (Number.isFinite(n)) return n;
  }
  const apps = apiResponse.apps;
  if (!Array.isArray(apps)) return 0;
  return apps.reduce((sum, row) => {
    const raw =
      row.dreameyDiamonds ??
      row.DreameyDiamonds ??
      row.totalDreameyCoins ??
      row.TotalDreameyCoins;
    const v = raw == null ? 0 : Number(raw);
    return sum + (Number.isFinite(v) ? v : 0);
  }, 0);
}

/**
 * DreameyDiamonds for Dreamey Vids only (single app row). Prefer AppId match, then name.
 */
export function dreameyDiamondsForDreameyVids(apps) {
  if (!Array.isArray(apps)) return 0;

  const coinsFrom = (row) => {
    if (!row || typeof row !== "object") return 0;
    const raw =
      row.dreameyDiamonds ??
      row.DreameyDiamonds ??
      row.totalDreameyCoins ??
      row.TotalDreameyCoins;
    if (raw == null) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const normName = (a) => String(a?.appName ?? a?.AppName ?? "").trim().toLowerCase();

  const byId = apps.find((a) => {
    const id = Number(a?.appId ?? a?.AppId);
    return Number.isFinite(id) && id === DREAMEY_VIDS_APP_ID;
  });
  if (byId) return coinsFrom(byId);

  const exact = apps.find((a) => normName(a) === "dreamey vids");
  if (exact) return coinsFrom(exact);

  const fuzzy = apps.find((a) => {
    const n = normName(a);
    return n.includes("dreamey") && (n.includes("vid") || n.includes("vids"));
  });
  if (fuzzy) return coinsFrom(fuzzy);

  return 0;
}

/**
 * Claimed rank tier ids for Dreamey Vids from GET /app-coins/apps (ClaimedDiamonds JSON on the app row).
 */
export function parseClaimedRankRewardIdsFromAppRow(row) {
  if (!row || typeof row !== "object") return [];
  const fromApi = row.claimedRankRewardIds ?? row.ClaimedRankRewardIds;
  if (Array.isArray(fromApi)) return fromApi.filter((x) => typeof x === "string");
  const raw = row.ClaimedDiamonds;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t || t === "[]") return [];
    try {
      const j = JSON.parse(t);
      return Array.isArray(j) ? j.filter((x) => typeof x === "string") : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function parseClaimedRankRewardIdsForDreameyVids(apps) {
  if (!Array.isArray(apps)) return [];
  const row = apps.find((a) => {
    const id = Number(a?.appId ?? a?.AppId);
    return Number.isFinite(id) && id === DREAMEY_VIDS_APP_ID;
  });
  return parseClaimedRankRewardIdsFromAppRow(row);
}

export function mergeUniqueStrings(a, b) {
  return [
    ...new Set([...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])]),
  ];
}

/** Client-side downline streak claims (instant UX; same pattern as rankDiamondClaims_). */
export const DOWNLINE_CLAIMS_STORAGE_PREFIX = "downlineDiamondClaims_v1_";

export const DOWNLINE_MILESTONE_COINS = {
  30: 10,
  90: 75,
  180: 200,
  365: 500,
};

export function downlineCoinsForMilestoneDays(milestoneDays) {
  const n = Number(milestoneDays);
  return Number.isFinite(n) ? DOWNLINE_MILESTONE_COINS[n] ?? 0 : 0;
}

export function readStoredDownlineClaims(userId) {
  if (userId == null) return [];
  try {
    const raw = localStorage.getItem(`${DOWNLINE_CLAIMS_STORAGE_PREFIX}${userId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((row) => {
        if (!row || typeof row !== "object") return null;
        const key = String(row.key ?? row.claimKey ?? "").trim();
        if (!key) return null;
        const coins = Number(row.coins);
        return {
          key,
          coins: Number.isFinite(coins) ? coins : downlineCoinsForMilestoneDays(row.milestoneDays),
        };
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

export function writeStoredDownlineClaims(userId, entries) {
  if (userId == null) return;
  try {
    const list = Array.isArray(entries) ? entries : [];
    const byKey = new Map();
    list.forEach((row) => {
      if (!row?.key) return;
      byKey.set(row.key, {
        key: row.key,
        coins: Number(row.coins) || downlineCoinsForMilestoneDays(row.milestoneDays),
      });
    });
    localStorage.setItem(
      `${DOWNLINE_CLAIMS_STORAGE_PREFIX}${userId}`,
      JSON.stringify(Array.from(byKey.values()))
    );
  } catch {
    /* quota / private mode */
  }
}

export function readStoredDownlineClaimKeys(userId) {
  return readStoredDownlineClaims(userId).map((row) => row.key);
}

export function appendStoredDownlineClaim(userId, { key, coins, milestoneDays }) {
  if (userId == null || !key) return;
  const nextCoins =
    Number.isFinite(Number(coins)) && Number(coins) > 0
      ? Number(coins)
      : downlineCoinsForMilestoneDays(milestoneDays);
  const existing = readStoredDownlineClaims(userId);
  if (existing.some((row) => row.key === key)) return;
  writeStoredDownlineClaims(userId, [...existing, { key, coins: nextCoins }]);
}

export function sumDownlineDiamondsFromStoredClaims(userId) {
  return readStoredDownlineClaims(userId).reduce(
    (sum, row) => sum + (Number(row.coins) || 0),
    0
  );
}

/** API Dreamey total plus locally recorded downline streak claims (instant on claim). */
export function dreameyDiamondsDisplayTotal(apiResponse, userId) {
  const base = dreameyDiamondsUserSum(apiResponse);
  if (userId == null) return base;
  return base + sumDownlineDiamondsFromStoredClaims(userId);
}
